'use client';

import { GraphQLWritingPreference } from '@sola/schema/dotcom/supporters/schema.generated.ts';

import {
  preferredUsernameQuery,
  supporterAddressQuery,
  supporterContactPreferencesQuery,
  supporterContactsQuery,
  writingPreferencesQuery,
} from '../queries.ts';
import { useSupporterQuery } from './base.ts';
import { useSupporterDetails } from './details.ts';

export const useAccountSettings = () => {
  // Contacts are non-blocking and can load after the page has rendered
  const {
    data: supporter,
    isPending: supporterPending,
    isError: supporterError,
  } = useSupporterDetails();
  const {
    data: supporterAddress,
    isPending: addressPending,
    isError: addressError,
  } = useSupporterQuery(supporterAddressQuery);
  const { data: preferredUsername, isPending: usernamePending } =
    useSupporterQuery(preferredUsernameQuery);
  const {
    data: contacts,
    isLoading: contactsPending,
    isError: contactsError,
  } = useSupporterQuery(supporterContactsQuery);

  return {
    supporter,
    supporterAddress,
    preferredUsername,
    contacts,
    hasError: supporterError || addressError || contactsError,
    showSkeleton:
      supporterPending || addressPending || usernamePending || contactsPending,
  };
};

export const useSupporterContacts = () => {
  const {
    data: contacts,
    isLoading: contactsPending,
    isError,
  } = useSupporterQuery(supporterContactPreferencesQuery);

  return {
    contacts,
    showSkeleton: contactsPending,
    isError,
  };
};

export const useContact = (contactId: string) => {
  const { contacts, showSkeleton, isError } = useSupporterContacts();
  const contact = contacts?.find(({ id }) => id === contactId);

  return {
    contact,
    showSkeleton,
    isError,
  };
};

export const useWritingPreferences = () => {
  const { data, isLoading, isPending, isError } = useSupporterQuery(
    writingPreferencesQuery,
  );

  // Upstream can return casing variants (e.g. 'ELECTRONIC' vs 'Electronic'),
  // so compare case-insensitively rather than relying on strict equality.
  const hasWritingPreference = (preference: GraphQLWritingPreference) =>
    data?.some((entry) => entry.toLowerCase() === preference.toLowerCase()) ??
    false;

  const hasEmail = hasWritingPreference(GraphQLWritingPreference.Electronic);
  const hasPrint = hasWritingPreference(GraphQLWritingPreference.DigitalPrint);

  return {
    hasEmail,
    hasPrint,
    isLoading,
    isPending,
    isError,
  };
};
